import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';

function collectProductNames(apiData) {
    const productNames = [];
    apiData.forEach(location => {
        location.productSummaryDtoList?.forEach(product => {
            const trimmedName = product.productName.trim();
            if (!productNames.includes(trimmedName)) {
                productNames.push(trimmedName);
            }
        });
    });
    return productNames;
}

function computeGrandTotals(apiData, productNames) {
    const grandTotals = {};
    productNames.forEach(productName => {
        grandTotals[productName] = { count: 0, amount: 0 };
    });

    apiData.forEach(location => {
        location.productSummaryDtoList?.forEach(product => {
            const trimmedName = product.productName.trim();
            if (grandTotals[trimmedName]) {
                grandTotals[trimmedName].count += product.count || 0;
                grandTotals[trimmedName].amount += product.amount || 0;
            }
        });
    });

    return grandTotals;
}

function applyCellStyle(cell, { font, fill, alignment, border }) {
    const style = {};

    if (font) {
        style.font = font;
    }
    if (fill) {
        style.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: fill } };
    }
    if (alignment) {
        style.alignment = alignment;
    }
    if (border) {
        style.border = border;
    }

    Object.assign(cell, { style });
}

function getThinBorder() {
    return {
        top: { style: 'thin' },
        left: { style: 'thin' },
        bottom: { style: 'thin' },
        right: { style: 'thin' },
    };
}

async function buildTransposedExcel({
    apiData,
    fileName,
    sheetName,
    locationKey,
    locationHeader
}) {
    try {
        const workbook = new ExcelJS.Workbook();
        const sheet = workbook.addWorksheet(sheetName, {
            views: [{ state: 'frozen', xSplit: 1, ySplit: 4 }],
        });

        const productNames = collectProductNames(apiData);
        const grandTotals = computeGrandTotals(apiData, productNames);

        const columns = [{ header: '', key: 'location', width: 25 }];
        productNames.forEach(productName => {
            columns.push(
                { header: '', key: `${productName}_count`, width: 18 },
                { header: '', key: `${productName}_amount`, width: 18 }
            );
        });
        sheet.columns = columns;

        const totalColumns = columns.length;

        // ---- TITLE ---- (row 1)
        const titleRow = sheet.getRow(1);
        titleRow.height = 25;
        titleRow.getCell(1).value = 'Other Deposits Report';
        sheet.mergeCells(1, 1, 1, totalColumns);
        applyCellStyle(titleRow.getCell(1), {
            font: { bold: true, size: 16 },
            alignment: { vertical: 'middle', horizontal: 'center' },
        });

        // ---- GAP ROW ---- (row 2)
        sheet.getRow(2).height = 15;

        // ---- PRODUCT NAMES ---- (row 3, each merged over 2 columns)
        const productRow = sheet.getRow(3);
        productRow.height = 20;

        const productRowValues = [''];
        productNames.forEach(productName => {
            productRowValues.push(productName, '');
        });
        productRow.values = productRowValues;

        let productColIndex = 2;
        productNames.forEach(productName => {
            sheet.mergeCells(3, productColIndex, 3, productColIndex + 1);
            productRow.getCell(productColIndex).value = productName;
            productColIndex += 2;
        });

        productRow.eachCell((cell, colNumber) => {
            if (colNumber > 1) {
                applyCellStyle(cell, {
                    font: { bold: true },
                    fill: 'FFD9E2F3',
                    alignment: { vertical: 'middle', horizontal: 'center' },
                    border: getThinBorder(),
                });
            }
        });

        // ---- COLUMN HEADERS ---- (row 4)
        const headerRow = sheet.getRow(4);
        headerRow.height = 18;

        const headers = [locationHeader];
        productNames.forEach(() => {
            headers.push('No of Account', 'Amount');
        });
        headerRow.values = headers;

        headerRow.eachCell(cell => {
            applyCellStyle(cell, {
                font: { bold: true },
                alignment: { vertical: 'middle', horizontal: 'center' },
                border: getThinBorder(),
            });
        });

        // ---- DATA ROWS ----
        apiData.forEach(location => {
            const rowData = { location: location[locationKey] };

            productNames.forEach(productName => {
                const product = location.productSummaryDtoList?.find(
                    p => p.productName.trim() === productName
                );
                rowData[`${productName}_count`] = product?.count || '';
                rowData[`${productName}_amount`] = product?.amount || '';
            });

            const row = sheet.addRow(rowData);
            row.height = 18;

            row.eachCell((cell, colNumber) => {
                applyCellStyle(cell, {
                    alignment: {
                        vertical: 'middle',
                        horizontal: colNumber === 1 ? 'left' : 'right',
                    },
                    border: getThinBorder(),
                });

                if (colNumber % 2 === 0) {
                    cell.numFmt = '#,##0';
                }
                if (colNumber > 1 && colNumber % 2 === 1) {
                    cell.numFmt = '#,##0.00';
                }
            });
        });

        // ---- GRAND TOTAL ROW ----
        const grandTotalRow = sheet.addRow(['GRAND TOTAL']);
        grandTotalRow.height = 20;

        let grandColIndex = 2;
        productNames.forEach(productName => {
            grandTotalRow.getCell(grandColIndex).value = grandTotals[productName].count;
            grandTotalRow.getCell(grandColIndex + 1).value = grandTotals[productName].amount;
            grandColIndex += 2;
        });

        grandTotalRow.eachCell((cell, colNumber) => {
            applyCellStyle(cell, {
                font: { bold: true },
                fill: 'FFFFE699',
                alignment: {
                    vertical: 'middle',
                    horizontal: colNumber === 1 ? 'left' : 'right',
                },
                border: getThinBorder(),
            });

            if (colNumber % 2 === 0) {
                cell.numFmt = '#,##0';
            }
            if (colNumber > 1 && colNumber % 2 === 1) {
                cell.numFmt = '#,##0.00';
            }
        });

        const buffer = await workbook.xlsx.writeBuffer();
        saveAs(new Blob([buffer]), fileName);
    } catch (error) {
        console.error('Excel generation failed:', error);
        throw error;
    }
}

export async function exportExcelDistrictWise({ apiData, fileName }) {
    return buildTransposedExcel({
        apiData,
        fileName,
        sheetName: 'District Wise Other Deposits',
        locationKey: 'districtName',
        locationHeader: 'District Name',
    });
}

export async function exportExcelBranchWise({ apiData, fileName }) {
    return buildTransposedExcel({
        apiData,
        fileName,
        sheetName: 'Branch Wise Other Deposits',
        locationKey: 'branchName',
        locationHeader: 'Branch Name',
    });
}
